//External libs
import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Subscription } from 'rxjs';

//Local libs
import Service from 'src/app/common/service';
import WaterReadingsModel from './waterReadings.model';

/**
 * @service WaterReadingsService
 * @description Service for managing water quality readings within the aquaculture system.
 * This service handles the retrieval and batch creation of water parameter logs.
 *
 * It interacts with the `/aqu/readings/` backend endpoint.
 *
 * **Key Responsibilities:**
 * 1. **Log Retrieval**: Fetching a comprehensive list of all water reading activities.
 * 2. **Batch Entry**: Supporting the bulk upload of readings for multiple ponds or parameters simultaneously.
 * 3. **Resource Management**: Handling HTTP request lifecycles to prevent memory leaks.
 *
 * @feature 1. Retrieve all water log activities.
 * @feature 2. Add batch water readings.
 * @feature 3. Robust management of concurrent HTTP requests.
 *
 * @architecture
 * Extends base Service<WaterReadingsModel> providing REST API integration.
 * Uses HttpClient for HTTP operations and manages subscriptions for cleanup.
 *
 * @service-endpoint /aqu/readings/
 * @service-provider root (singleton, application-wide)
 *
 * @author AquaCloud Development Team
 * @version 1.0.0
 */
@Injectable({
  providedIn: 'root'
})
export class WaterReadingsService extends Service<WaterReadingsModel> {

  /**
   * @type {Array<Subscription>}
   * @description Array tracking all ongoing HTTP request subscriptions.
   * Used to maintain references to active Observables so they can be unsubscribed
   * when the service is destroyed or when cancellation is requested.
   * @default []
   * @side-effect Used to store subscription references for cancellation via cancelOnGoingHttpRequests()
   */
  public ongoingRequests: Subscription[] = [];

  /**
   * @method constructor
   * @description Service constructor with dependency injection of HttpClient.
   *
   * Initializes the service by calling the parent Service constructor with the
   * HttpClient instance and the specific API endpoint path (`/aqu/readings/`).
   *
   * @param {HttpClient} httpClient - @service Angular HTTP client for making HTTP requests to the backend.
   * @returns {void}
   */
  constructor(public httpClient: HttpClient) {
    super(httpClient, '/blubay/readings/');
  }

  /**
   * @method getAllWaterLogActivities
   * @async
   * @description Fetch all water reading log activities from the backend.
   *
   * This method performs a GET request to the service endpoint. It is configured to retrieve
   * a large number of records (pageSize=2000) to ensure a comprehensive history is loaded.
   *
   * @returns {Promise<Array<WaterReadingsModel>>} Promise resolving to an array of WaterReadingsModel objects.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP GET request.
   * - Pushes the subscription to `ongoingRequests`.
   *
   * @example
   * waterReadingsService.getAllWaterLogActivities().then(logs => {
   *   console.log('Water Logs:', logs);
   * });
   */
  public getAllWaterLogActivities() {
    return new Promise<Array<WaterReadingsModel>>((resolve, reject) => {
      const request = this.httpClient.get<Array<WaterReadingsModel>>(`${this.baseUrl}${this.serviceUrl}?pageSize=2000`, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  /**
   * @method addBatchWaterReadings
   * @async
   * @description Submit a batch of water readings for creation.
   *
   * This is used for bulk data entry, allowing multiple readings to be recorded in a single transaction.
   *
   * @param {WaterReadingsModel} readings - The model containing the batch reading data.
   * @returns {Promise<WaterReadingsModel>} Promise resolving to the created reading data.
   * @throws {HttpErrorResponse} Rejects the promise if the HTTP request fails.
   * @side-effect
   * - Initiates an HTTP POST request to `batchEntry`.
   * - Pushes the subscription to `ongoingRequests`.
   */
  public addBatchWaterReadings(readings: WaterReadingsModel) {
    return new Promise<WaterReadingsModel>((resolve, reject) => {
      const request = this.httpClient.post<WaterReadingsModel>(`${this.baseUrl}${this.serviceUrl}batchEntry`, readings, this.httpOptions).subscribe(response => {
        resolve(response);
      }, (error: HttpErrorResponse) => {
        reject(error);
      });
      this.ongoingRequests.push(request);
    });
  }

  //To Cancel the onGoing Http Requests
  /**
   * @method cancelOnGoingHttpRequests
   * @description Cancel all ongoing/pending HTTP requests to prevent memory leaks.
   *
   * This method iterates through the `ongoingRequests` array and calls `unsubscribe()`
   * on each subscription. This is typically called when a component using this service
   * is destroyed (ngOnDestroy) to ensure that no network requests continue in the background.
   *
   * @returns {void}
   * @side-effect
   * - Aborts all pending HTTP requests tracked in `ongoingRequests`.
   * - Resets `ongoingRequests` to an empty array.
   */
  public cancelOnGoingHttpRequests(): void {
    this.ongoingRequests.forEach((request) => {
      if (request && typeof request.unsubscribe === 'function') {
        request.unsubscribe();
      }
    });
    this.ongoingRequests = [];
  }
}
